import { Icon, Skeleton as MuiSkeleton, SkeletonProps, Stack } from '@mui/material';
import { HeaderProps, PageHeader } from '@schooly/components/filters';
import { FC, PropsWithChildren } from 'react';

import { MarkIcon } from '../../assets/icons';
import { GridBody, GridCell, GridHead, GridRow, MainPageGrid } from '../Grid';

const PAGE_SIZE = 30;

export type ColumnSpec = {
  widthPercent: number;
  skeletonWidthPercent?: number;
};

type PageSkeletonProps = {
  pageSize?: number;
  columns: ColumnSpec[];
  rowHeight?: number;
  withHeader?: boolean;
  withSearch?: boolean;
} & HeaderProps;

export const PageSkeleton: FC<PropsWithChildren<PageSkeletonProps>> = ({
  pageSize = PAGE_SIZE,
  children,
  columns,
  rowHeight = 54,
  withHeader = false,
  withSearch = true,
  ...headerProps
}) => {
  return (
    <Stack
      gap={1}
      sx={{
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {withHeader && <HeaderSkeleton {...headerProps} withSearch={withSearch} />}
      {children}

      <MainPageGrid
        sx={(theme) => ({
          height: '100%',
          '& .MuiTableCell-root': {
            borderColor: `${theme.palette.divider} !important`,
          },
        })}
      >
        <GridHead borderBottom>
          <GridRow>
            {columns.map((col, index) => (
              <GridCell
                className={`column-cell-${index}`}
                noVerticalPadding
                key={index}
                sx={{ width: `${col.widthPercent}%` }}
              >
                <Stack direction="row" alignItems="center" gap={1} sx={{ py: 0 }}>
                  {index !== 0 && (
                    <Skeleton width={`${col.skeletonWidthPercent ?? 90}%`} height={16} />
                  )}
                </Stack>
              </GridCell>
            ))}
          </GridRow>
        </GridHead>
        <GridBody>
          {Array.from({ length: pageSize }).map((_, rowIndex) => (
            <GridRow sx={{ height: rowHeight }} key={rowIndex} withoutDefaultHover>
              {columns.map((col, colIndex) => {
                return (
                  <GridCell
                    className={`row-cell-${colIndex}`}
                    key={colIndex}
                    sx={{ minWidth: 120, width: `${col.widthPercent}%`, p: 1 }}
                  >
                    <Skeleton width={`${col.skeletonWidthPercent ?? 90}%`} height={16} />
                  </GridCell>
                );
              })}
            </GridRow>
          ))}
        </GridBody>
      </MainPageGrid>
    </Stack>
  );
};

export const GridSkeleton: FC<{ pageSize: number; columns: ColumnSpec[] }> = ({
  pageSize,
  columns,
}) => {
  return (
    <>
      {Array.from({ length: pageSize }).map((_, rowIndex) => (
        <GridRow sx={{ height: 54 }} key={rowIndex} withoutDefaultHover>
          {columns?.map((col, colIndex) => {
            return (
              <GridCell
                key={colIndex}
                className={`row-cell-${colIndex}`}
                sx={{ minWidth: 120, width: `${col.widthPercent}%` }}
              >
                <Skeleton width={`${col.skeletonWidthPercent ?? 90}%`} height={16} />
              </GridCell>
            );
          })}
        </GridRow>
      ))}
    </>
  );
};

export const HeaderSkeleton: FC<HeaderProps & { withSearch?: boolean }> = ({
  withSearch = true,
  ...props
}) => {
  return (
    <Stack gap={1}>
      <PageHeader {...props}>{withSearch && <Skeleton variant="rectangular" />}</PageHeader>

      <FiltersSkeleton />
    </Stack>
  );
};

export const FiltersSkeleton: FC = () => {
  return (
    <Stack direction="row" alignItems="center" gap={2}>
      <Icon className="mark-icon" sx={(theme) => ({ width: 30, color: theme.palette.common.grey })}>
        <MarkIcon />
      </Icon>
      <Skeleton width={196} height={31} />
      <Skeleton width={96} height={31} />
    </Stack>
  );
};

export const Skeleton: FC<SkeletonProps> = (props) => {
  return <MuiSkeleton variant="rectangular" sx={{ borderRadius: '4px !important' }} {...props} />;
};
